import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { SITE_URL } from "@/lib/seo";
import { authorByName } from "@/content/authors";
import { blogPosts, formatPostDate, loadPostBody, postBySlug, type BlogPost } from "@/content/blog";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }): Promise<{ post: BlogPost; body: string }> => {
    const post = postBySlug(params.slug);
    if (!post) throw notFound();
    const body = await loadPostBody(params.slug);
    if (body === null) throw notFound();
    return { post, body };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Article not found | Leadership Edge Live" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { post } = loaderData;
    const title = post.seoTitle || `${post.title} | Leadership Edge Live`;
    const description = post.seoDescription || post.excerpt;
    return {
      links: [{ rel: "canonical", href: `${SITE_URL}/blog/${params.slug}/` }],
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  notFoundComponent: () => <ArticleMissing />,
  component: BlogPostPage,
});

function ArticleMissing() {
  return (
    <Section tone="default">
      <div className="mx-auto max-w-xl py-16 text-center">
        <h1 className="font-display text-3xl text-ink">We couldn't find that article.</h1>
        <Link
          to="/blog/"
          className="mt-6 inline-flex items-center rounded-[6px] bg-[#693D94] px-5 py-2.5 text-sm font-semibold text-primary-foreground"
        >
          Back to Insights
        </Link>
      </div>
    </Section>
  );
}

function BlogPostPage() {
  const { post, body } = Route.useLoaderData();
  const author = authorByName(post.author);
  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <>
      <article>
        <header className="border-b border-border/60 bg-lavender">
          <div className="mx-auto max-w-3xl px-6 pb-12 pt-14 md:pt-20">
            <Link
              to="/blog/"
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All articles
            </Link>
            <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-primary">
              {post.category}
            </p>
            <h1 className="mt-3 font-display text-3xl leading-tight text-ink md:text-4xl">
              {post.title}
            </h1>
            <div className="mt-6 flex items-center gap-3">
              <img src={post.authorAvatar} alt="" className="h-10 w-10 rounded-full object-cover" />
              <p className="text-sm text-muted-foreground">
                {author ? (
                  <Link
                    to="/author/$slug/"
                    params={{ slug: author.slug }}
                    className="font-semibold text-primary hover:underline"
                  >
                    {post.author}
                  </Link>
                ) : (
                  post.author
                )}
                <span className="px-1.5 text-border">|</span>
                {formatPostDate(post.date)}
                <span className="px-1.5 text-border">|</span>
                {post.readTime}
              </p>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-3xl px-6">
          <img
            src={post.image}
            alt={post.imageAlt}
            className="-mt-0 aspect-[16/9] w-full rounded-2xl object-cover md:-mt-8"
          />
          <div className="blog-body py-10 md:py-14" dangerouslySetInnerHTML={{ __html: body }} />
        </div>
      </article>

      <Section tone="lavender">
        <h2 className="mb-8 font-display text-2xl text-ink md:text-3xl">Keep reading</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {related.map((p) => (
            <Link
              key={p.slug}
              to="/blog/$slug/"
              params={{ slug: p.slug }}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-background transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-brand"
            >
              <div className="aspect-[16/10] w-full overflow-hidden bg-lavender">
                <img
                  src={p.image}
                  alt={p.imageAlt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {p.category}
                </p>
                <h3 className="mt-2 font-display text-base leading-snug text-ink">{p.title}</h3>
                <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                  Read article
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <AdvisorCTA
        title="Want help turning this into practice?"
        body="A Development Pro can point you to the behavior worth working on first."
      />
    </>
  );
}
